import axios from 'axios';
import io from 'socket.io-client';
import { message, Modal, notification } from 'antd';
import { action, observable, runInAction } from 'mobx';
const confirm = Modal.confirm;

export class MakoBotStore {
    @observable config = null;
    @observable connected = false;
    @observable exchangeConnected = false;
    @observable direction = null;
    @observable position = null;
    @observable ticker = null;
    @observable exchangePosition = null;
    @observable positions = [];
    @observable performance = [];
    @observable pnl = [];
    @observable tzone = 0;
    @observable scalperTa = {};
    @observable mmTa = {};
    constructor({ rootStore }) {
        this.rootStore = rootStore;
    }

    get connectionURI() {
        return this.config.env === 'local'
            ? `//${this.config.location}`
            : `https://${this.config.exchange}.apextrader.io/mako-${this.config._id}`;
    }

    async proxy(endpoint) {
        const res = await axios.get(`${this.connectionURI}/${endpoint}/`);
        return res.data;
    }

    @action closeStream(id) {
        this.socket.close();
        this.config = null;
        this.connected = false;
        this.exchangeConnected = false;
        this.position = null;
        this.positions = [];
        this.performance = [];
        this.ticker = null;
        this.direction = null;
    }

    @action connectToStream(id) {
        this.botId = this.config._id;
        this.socket = io(
            this.config.env === 'local'
                ? `//${this.config.location}`
                : `https://${this.config.exchange}.apextrader.io/`,
            {
                path: this.config.env === 'local' ? '' : `/mako-${this.config._id}/socket.io/`,
                query: `token=${this.rootStore.userStore.idToken}`
            }
        );

        this.socket.on('connect', () => {
            console.log('connected');
            runInAction(() => {
                this.connected = true;
            });
        });

        this.socket.on('disconnect', () => {
            console.log('disconnected');
            runInAction(() => {
                this.connected = false;
            });
        });

        this.socket.on('position', ({ data }) => {
            runInAction(() => {
                if (!data && this.position != null) {
                    this.getPositions(this.config._id); //position closed
                }
                this.position = data;
            });
        });

        this.socket.on('state', ({ data }) => {
            runInAction(() => {
                this.exchangeConnected = data.exchangeConnected;
            });
        });

        this.socket.on('exchange:connected', ({ data }) => {
            runInAction(() => {
                this.exchangeConnected = data;
            });
        });

        this.socket.on('exchange:connected', ({ data }) => {
            runInAction(() => {
                this.exchangeConnected = data;
            });
        });

        this.socket.on('exchange:position', ({ data }) => {
            runInAction(() => {
                this.exchangePosition = data;
            });
        });

        this.socket.on('ticker', ({ data }) => {
            runInAction(() => {
                this.ticker = data;
            });
        });

        this.socket.on('direction', ({ data }) => {
            runInAction(() => {
                this.direction = data;
            });
        });

        this.socket.on('realized-pnl', ({ data }) => {
            runInAction(() => {
                this.pnl = data;
            });
        });

        this.socket.on('tzone', ({ data }) => {
            runInAction(() => {
                this.tzone = data;
            });
        });

        this.socket.on('config-refresh', ({ data }) => {
            runInAction(() => {
                this.getConfig(data.id);
            });
        });

        this.socket.on('notification', ({ data }) => {
            this.handleNotifications(data);
        });

        this.socket.on('scalper-ta', ({ data }) => {
            runInAction(() => {
                this.scalperTa = data;
            });
        });

        this.socket.on('mm-ta', ({ data }) => {
            runInAction(() => {
                this.mmTa = data;
            });
        });
    }

    handleNotifications({ title, body }) {
        console.log('notification', title, body);
        const audio = new Audio(`/alert.mp3`);
        audio.play().catch(e => console.log(e));

        if (!('Notification' in window)) {
            notification.success({
                message: title,
                description: body
            });
        }

        // Let's check whether notification permissions have already been granted
        else if (Notification.permission === 'granted') {
            // If it's okay let's create a notification
            new Notification(title, {
                body,
                icon: 'https://console.apextrader.io/android-chrome-192x192.png'
            });
        }

        // Otherwise, we need to ask the user for permission
        else if (Notification.permission !== 'denied') {
            Notification.requestPermission().then(function(permission) {
                // If the user accepts, let's create a notification
                if (permission === 'granted') {
                    new Notification(title, {
                        body,
                        icon: 'https://console.apextrader.io/android-chrome-192x192.png'
                    });
                }
            });
        } else {
            notification.success({
                message: title,
                description: body
            });
        }
    }

    async getPositions(id) {
        const res = await axios.get(`${this.rootStore.env.botApi}${id}/positions`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
        runInAction(() => {
            this.positions = res.data;
        });
    }

    async getPerformance(id) {
        const res = await axios.get(`${this.rootStore.env.botApi}${id}/performance`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
        runInAction(() => {
            this.performance = res.data;
        });
    }

    async getConfig(id) {
        const res = await axios.get(`${this.rootStore.env.botApi}${id}`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
        runInAction(() => {
            this.config = res.data;
        });
    }

    async updateConfig(data) {
        try {
            if (!data.api_key) delete data.api_key;
            if (!data.api_secret) delete data.api_secret;

            const res = await axios.post(`${this.rootStore.env.botApi}${this.botId}`, data, {
                headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
            });
            runInAction(() => {
                this.config = res.data;
                message.success('Configuration updated');
            });
        } catch (e) {
            message.error(`There was an error updating: ${e.message}`);
        }
    }

    async reboot() {
        await axios.get(`${this.rootStore.env.botApi}mako/${this.botId}/reboot`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
        notification.success({
            message: 'Bot rebooting',
            description: 'This may take a few minutes.'
        });
    }

    async getKline(symbol, resolution, from, to) {
        return this.proxy(`v1/kline/${symbol}/${resolution}?from=${from}&to=${to}`);
    }

    streamKline(symbol, resolution) {
        this.socket.emit('message', { channel: 'candles:stream', symbol, resolution });
    }

    addEntryLayer(layer) {
        this.socket.emit('message', { channel: 'position:add-layer', ...layer });
    }

    updateEntryLayer(orderId, price) {
        this.socket.emit('message', { channel: 'position:update-layer', orderId, price });
    }
    cancelEntryLayer(orderId) {
        this.socket.emit('message', { channel: 'position:cancel-layer', orderId });
    }

    updateExitLayer(orderId, price) {
        this.socket.emit('message', { channel: 'position:update-exit', orderId, price });
    }
}



// WEBPACK FOOTER //
// ./src/stores/MakoBot/index.js